import React, { useState, useEffect, cloneElement } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  Avatar,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Stack,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  NotificationImportant as NotificationIcon,
  CheckCircle as CheckCircleIcon,
  PersonAdd as PersonAddIcon,
  RoomService,
  Edit as EditIcon,
  Help as HelpIcon,
  Receipt as ReceiptIcon,
  NotificationImportant as BellIcon,
  Message as MessageIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  ViewList as ListViewIcon,
  AccountTree as TimelineViewIcon,
  Restaurant as RestaurantIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import { timelineApi } from '../api/api';
import type { 
  NotificationEvent
} from '../types/notification.types';
import {
  EventType,
  RequestType,
  REQUEST_TYPE_LABELS
} from '../types/notification.types';
import { useSSE } from '../services/sseService';
import { AppIconButton } from './ui';

interface VendorTimelineProps {
  vendorId: number;
}

const EVENT_TYPE_LABELS: Record<EventType, string> = {
  [EventType.CUSTOMER_REQUEST]: 'Customer Request',
  [EventType.STEWARD_ACTION]: 'Steward Action',
  [EventType.ORDER_STATUS_CHANGE]: 'Order Update',
  [EventType.SYSTEM_EVENT]: 'System Event'
};

const EVENT_TYPE_ICONS: Record<EventType, React.ReactElement> = {
  [EventType.CUSTOMER_REQUEST]: <NotificationIcon />,
  [EventType.STEWARD_ACTION]: <PersonAddIcon />,
  [EventType.ORDER_STATUS_CHANGE]: <RestaurantIcon />,
  [EventType.SYSTEM_EVENT]: <BellIcon />
};

const EVENT_TYPE_COLORS: Record<EventType, 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'> = {
  [EventType.CUSTOMER_REQUEST]: 'warning',
  [EventType.STEWARD_ACTION]: 'info',
  [EventType.ORDER_STATUS_CHANGE]: 'primary',
  [EventType.SYSTEM_EVENT]: 'secondary'
};

export const VendorTimeline: React.FC<VendorTimelineProps> = ({ vendorId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [events, setEvents] = useState<NotificationEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'timeline' | 'list'>('timeline');
  const [filters, setFilters] = useState({
    tableId: '',
    orderId: '',
    eventType: '',
    dateRange: 'today'
  });
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshKey(k => k + 1);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Connect to SSE for real-time updates
  useSSE({
    endpoint: 'vendor',
    id: vendorId,
    onEvent: (type, data) => {
      console.log('[VendorTimeline] SSE event:', type, data);
      
      // Refresh timeline when events are received
      if (type === 'event' || type === 'customer-request') {
        setRefreshKey(k => k + 1);
      }
    },
    onConnect: () => {
      console.log('[VendorTimeline] SSE connected');
    },
    onError: (error) => {
      console.error('[VendorTimeline] SSE error:', error);
    }
  });

  // Fetch events
  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      setError(null);

      try {
        const params: any = {
          page,
          size: 20
        };

        if (filters.tableId) params.tableId = filters.tableId;
        if (filters.orderId) params.orderId = filters.orderId;
        if (filters.eventType) params.eventType = filters.eventType;

        const response = await timelineApi.getVendorTimeline(vendorId, params);
        
        if (page === 0) {
          setEvents(response.data.content || []);
        } else {
          setEvents(prev => [...prev, ...(response.data.content || [])]);
        }
        
        setHasMore(!response.data.last);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to fetch timeline');
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [vendorId, filters, page, refreshKey]);

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleViewModeChange = (event: React.MouseEvent<HTMLElement>, newMode: 'timeline' | 'list' | null) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const handleLoadMore = () => {
    setPage(prev => prev + 1);
  };

  const formatEventTime = (dateString: string) => {
    const date = new Date(dateString);
    return {
      time: format(date, 'HH:mm'),
      date: format(date, 'MMM dd'),
      relative: formatDistanceToNow(date, { addSuffix: true })
    };
  };

  const getEventIcon = (event: NotificationEvent) => {
    // Check if it's a customer request type in metadata
    const requestType = event.metadata?.requestType;
    if (requestType && REQUEST_TYPE_LABELS[requestType as RequestType]) {
      switch (requestType) {
        case 'TABLE_SERVICE': return <RoomService />;
        case 'ORDER_MODIFICATION': return <EditIcon />;
        case 'ASSISTANCE': return <HelpIcon />;
        case 'BILL_REQUEST': return <ReceiptIcon />;
        case 'CALL_STEWARD': return <BellIcon />;
        case 'CUSTOM_MESSAGE': return <MessageIcon />;
      }
    }
    
    return EVENT_TYPE_ICONS[event.eventType];
  };

  const renderTimelineView = () => {
    // iPhone message style for mobile
    if (isMobile && viewMode === 'timeline') {
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: 2,
          px: 1,
          pb: 2
        }}>
          {events.map((event, index) => {
            const timeInfo = formatEventTime(event.createdAt);
            const isCustomerRequest = event.eventType === EventType.CUSTOMER_REQUEST;
            const isStewardAction = event.eventType === EventType.STEWARD_ACTION;
            
            return (
              <Box key={event.id}>
                {/* Date separator */}
                {(index === 0 || 
                  formatEventTime(events[index - 1].createdAt).date !== timeInfo.date) && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block',
                      textAlign: 'center',
                      color: 'text.secondary',
                      my: 2
                    }}
                  >
                    {timeInfo.date}
                  </Typography>
                )}
                
                {/* Message bubble */}
                <Box sx={{ 
                  display: 'flex',
                  justifyContent: isCustomerRequest ? 'flex-start' : 'flex-end',
                  mb: 1
                }}>
                  <Box sx={{ 
                    maxWidth: '85%',
                    display: 'flex',
                    flexDirection: isCustomerRequest ? 'row' : 'row-reverse',
                    alignItems: 'flex-end',
                    gap: 1
                  }}>
                    {/* Avatar */}
                    <Avatar 
                      sx={{ 
                        width: 32,
                        height: 32,
                        bgcolor: theme.palette[EVENT_TYPE_COLORS[event.eventType]].main
                      }}
                    >
                      {cloneElement(getEventIcon(event), { fontSize: 'small' })}
                    </Avatar>
                    
                    {/* Message content */}
                    <Box>
                      <Box sx={{
                        bgcolor: isCustomerRequest 
                          ? 'grey.200' 
                          : theme.palette[EVENT_TYPE_COLORS[event.eventType]].light,
                        color: isCustomerRequest 
                          ? 'text.primary' 
                          : theme.palette[EVENT_TYPE_COLORS[event.eventType]].contrastText,
                        borderRadius: 2,
                        px: 2,
                        py: 1.5,
                        position: 'relative',
                        '&::before': isCustomerRequest ? {
                          content: '""',
                          position: 'absolute',
                          bottom: '0',
                          left: '-8px',
                          width: '0',
                          height: '0',
                          borderStyle: 'solid',
                          borderWidth: '0 8px 8px 0',
                          borderColor: 'transparent grey.200 transparent transparent'
                        } : {
                          content: '""',
                          position: 'absolute',
                          bottom: '0',
                          right: '-8px',
                          width: '0',
                          height: '0',
                          borderStyle: 'solid',
                          borderWidth: '0 0 8px 8px',
                          borderColor: `transparent transparent transparent ${theme.palette[EVENT_TYPE_COLORS[event.eventType]].light}`
                        }
                      }}>
                        <Typography variant="subtitle2" gutterBottom>
                          {event.title}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {event.description}
                        </Typography>
                        
                        {/* Metadata chips */}
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1 }}>
                          {event.tableName && (
                            <Chip 
                              label={event.tableName} 
                              size="small" 
                              sx={{ 
                                height: '20px',
                                fontSize: '0.7rem',
                                bgcolor: isCustomerRequest ? 'grey.300' : 'rgba(255,255,255,0.3)'
                              }}
                            />
                          )}
                          {event.stewardName && (
                            <Chip 
                              label={event.stewardName} 
                              size="small" 
                              sx={{ 
                                height: '20px',
                                fontSize: '0.7rem',
                                bgcolor: isCustomerRequest ? 'grey.300' : 'rgba(255,255,255,0.3)'
                              }}
                            />
                          )}
                        </Stack>
                      </Box>
                      
                      {/* Time */}
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          display: 'block',
                          textAlign: isCustomerRequest ? 'left' : 'right',
                          color: 'text.secondary',
                          mt: 0.5,
                          px: 1
                        }}
                      >
                        {timeInfo.time}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      );
    }
    
    // Desktop timeline view remains the same
    return (
      <Timeline position={isMobile ? "right" : "alternate"}>
        {events.map((event, index) => {
          const timeInfo = formatEventTime(event.createdAt);
          const isLastItem = index === events.length - 1;
          
          return (
            <TimelineItem key={event.id}>
              {!isMobile && (
                <TimelineOppositeContent
                  sx={{ m: 'auto 0' }}
                  align={index % 2 === 0 ? "right" : "left"}
                  variant="body2"
                  color="text.secondary"
                >
                  <Typography variant="caption" display="block">
                    {timeInfo.date}
                  </Typography>
                  <Typography variant="body2">
                    {timeInfo.time}
                  </Typography>
                </TimelineOppositeContent>
              )}
              
              <TimelineSeparator>
                <TimelineConnector sx={{ bgcolor: 'grey.300' }} />
                <TimelineDot color={EVENT_TYPE_COLORS[event.eventType]} variant="outlined">
                  {getEventIcon(event)}
                </TimelineDot>
                {!isLastItem && <TimelineConnector sx={{ bgcolor: 'grey.300' }} />}
              </TimelineSeparator>
              
              <TimelineContent sx={{ py: '12px', px: 2 }}>
                <Card elevation={isMobile ? 1 : 3}>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography variant="h6" component="span">
                        {event.title}
                      </Typography>
                      {isMobile && (
                        <Typography variant="caption" color="text.secondary">
                          {timeInfo.relative}
                        </Typography>
                      )}
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {event.description}
                    </Typography>
                    
                    <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
                      {event.tableName && (
                        <Chip 
                          label={`Table: ${event.tableName}`} 
                          size="small" 
                          variant="outlined"
                        />
                      )}
                      {event.userName && (
                        <Chip 
                          label={`Customer: ${event.userName}`} 
                          size="small" 
                          variant="outlined"
                        />
                      )}
                      {event.stewardName && (
                        <Chip 
                          label={`Steward: ${event.stewardName}`} 
                          size="small" 
                          variant="outlined"
                          color="info"
                        />
                      )}
                      {event.orderId && (
                        <Chip 
                          label={`Order #${event.orderId}`} 
                          size="small" 
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
    );
  };

  const renderListView = () => (
    <List>
      {events.map((event, index) => {
        const timeInfo = formatEventTime(event.createdAt);
        
        return (
          <React.Fragment key={event.id}>
            <ListItem alignItems="flex-start">
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: theme.palette[EVENT_TYPE_COLORS[event.eventType]].main }}>
                  {getEventIcon(event)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle1">
                      {event.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {timeInfo.relative}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {event.description}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip 
                        label={EVENT_TYPE_LABELS[event.eventType]} 
                        size="small" 
                        color={EVENT_TYPE_COLORS[event.eventType]}
                      />
                      {event.tableName && (
                        <Chip label={`Table: ${event.tableName}`} size="small" variant="outlined" />
                      )}
                      {event.stewardName && (
                        <Chip label={`Steward: ${event.stewardName}`} size="small" variant="outlined" />
                      )}
                    </Stack>
                  </Box>
                }
              />
            </ListItem>
            {index < events.length - 1 && <Divider variant="inset" component="li" />}
          </React.Fragment>
        );
      })}
    </List>
  );

  return (
    <Box>
      {/* Header and Filters */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, backgroundColor: 'background.default' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h5" component="h2">
            Event Timeline
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
            >
              <ToggleButton value="timeline">
                <TimelineViewIcon />
              </ToggleButton>
              <ToggleButton value="list">
                <ListViewIcon />
              </ToggleButton>
            </ToggleButtonGroup>
            <AppIconButton
              icon={<RefreshIcon />}
              tooltip="Refresh"
              variant="subtle"
              onClick={() => setRefreshKey(k => k + 1)}
            />
          </Box>
        </Box>

        {/* Filters */}
        <Stack direction={isMobile ? "column" : "row"} spacing={2}>
          <TextField
            label="Table ID"
            value={filters.tableId}
            onChange={(e) => handleFilterChange('tableId', e.target.value)}
            size="small"
            sx={{ minWidth: 120 }}
          />
          <TextField
            label="Order ID"
            value={filters.orderId}
            onChange={(e) => handleFilterChange('orderId', e.target.value)}
            size="small"
            sx={{ minWidth: 120 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Event Type</InputLabel>
            <Select
              value={filters.eventType}
              onChange={(e) => handleFilterChange('eventType', e.target.value)}
              label="Event Type"
            >
              <MenuItem value="">All</MenuItem>
              {Object.entries(EVENT_TYPE_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={filters.dateRange}
              onChange={(e) => handleFilterChange('dateRange', e.target.value)}
              label="Date Range"
            >
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="week">This Week</MenuItem>
              <MenuItem value="month">This Month</MenuItem>
              <MenuItem value="all">All Time</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Content */}
      {loading && events.length === 0 ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : events.length === 0 ? (
        <Alert severity="info">
          No events found for the selected filters
        </Alert>
      ) : (
        <>
          {viewMode === 'timeline' ? renderTimelineView() : renderListView()}
          
          {hasMore && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                disabled={loading}
                startIcon={loading && <CircularProgress size={20} />}
              >
                Load More
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};